import React, { useState, useEffect } from "react";
import NapkinCheckoutForm from "./NapkinCheckoutForm";
import StripeProvider from "./StripeProvider";
import "../../Styles/Billing/Billing.css";
import { FaTimes as _FaTimes } from "react-icons/fa";
import StripeLogo from "../../Assets/Images/Stripe-black.png";
import type { PlanTier, BillingFrequency, ProrationEstimate } from "../../api/billing";
import {
  changePlan as changePlanAPI,
  estimateProration,
  contactEnterprise,
} from "../../api/billing";

const FaTimes = _FaTimes as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

export interface NapkinSubscriptionModalProps {
  isOpen: boolean;
  currentTier: PlanTier;
  currentFrequency: BillingFrequency | null;
  hasSubscription: boolean;
  renewalDate?: string;
  onClose: () => void;
  onCancel: () => void;
  onSuccess: (d: any) => void;
  onError: (e: any) => void;
}

type ModalView =
  | "compare"
  | "checkout"
  | "manage"
  | "proration"
  | "enterprise";

// ---------- Tier pricing data ----------
const PRICING: Record<
  "pro" | "business",
  { monthly: number; annual: number; annualTotal: number }
> = {
  pro: { monthly: 12.99, annual: 7.99, annualTotal: 95.88 },
  business: { monthly: 29.99, annual: 19.99, annualTotal: 239.88 },
};

const TIER_ORDER: PlanTier[] = ["free", "pro", "business", "enterprise"];

const TIER_FEATURES: Record<PlanTier, string[]> = {
  free: [
    "3 signature requests/month",
    "5 templates",
    "1 user",
    "Basic audit trail",
  ],
  pro: [
    "Unlimited signatures",
    "All templates",
    "Quick Send",
    "1 user",
    "Full audit trail",
    "Email support",
  ],
  business: [
    "Everything in Pro",
    "AI-powered features",
    "Unlimited Quick Send templates",
    "Up to 10 users",
    "Priority support",
  ],
  enterprise: [
    "Everything in Business",
    "Unlimited users",
    "Custom integrations",
    "Dedicated account manager",
    "SLA & uptime guarantee",
  ],
};

// ---------- Component ----------
const NapkinSubscriptionModal: React.FC<NapkinSubscriptionModalProps> = ({
  isOpen,
  currentTier,
  currentFrequency,
  hasSubscription,
  renewalDate,
  onClose,
  onCancel,
  onSuccess,
  onError,
}) => {
  const [view, setView] = useState<ModalView>(
    hasSubscription ? "manage" : "compare"
  );
  const [frequency, setFrequency] = useState<BillingFrequency>(
    currentFrequency || "annual"
  );
  const [selectedTier, setSelectedTier] = useState<PlanTier | null>(null);

  // Proration state
  const [prorationData, setProrationData] = useState<ProrationEstimate | null>(null);
  const [prorationLoading, setProrationLoading] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);

  // Enterprise contact form
  const [entCompany, setEntCompany] = useState("");
  const [entTeamSize, setEntTeamSize] = useState("");
  const [entMessage, setEntMessage] = useState("");
  const [entSubmitting, setEntSubmitting] = useState(false);
  const [entDone, setEntDone] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setView(hasSubscription ? "manage" : "compare");
      setFrequency(currentFrequency || "annual");
      setSelectedTier(null);
      setProrationData(null);
      setEntDone(false);
    }
  }, [isOpen, hasSubscription, currentFrequency]);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // ---------- Helpers ----------
  const tierRank = (t: PlanTier) => TIER_ORDER.indexOf(t);
  const isUpgrade = (from: PlanTier, to: PlanTier) => tierRank(to) > tierRank(from);

  const formatPrice = (cents: number) => {
    const dollars = Math.abs(cents) / 100;
    return `$${dollars.toFixed(2)}`;
  };

  const tierLabel = (t: PlanTier) =>
    t === "free" ? "Free" : t === "pro" ? "Pro" : t === "business" ? "Business" : "Enterprise";

  // ---------- Handlers ----------
  const handleSelectTier = async (tier: PlanTier) => {
    if (tier === "enterprise") {
      setView("enterprise");
      return;
    }
    if (tier === "free") return; // Can't subscribe to free

    setSelectedTier(tier);

    // If user already has a subscription, show proration preview
    if (hasSubscription && (tier !== currentTier || frequency !== currentFrequency)) {
      setProrationLoading(true);
      setView("proration");
      try {
        const est = await estimateProration(tier, frequency);
        setProrationData(est);
      } catch (err: any) {
        onError(err);
        setView("compare");
      } finally {
        setProrationLoading(false);
      }
    } else {
      // New subscription — go to checkout
      setView("checkout");
    }
  };

  const handleConfirmPlanChange = async () => {
    if (!selectedTier) return;
    setChangingPlan(true);
    try {
      const result = await changePlanAPI(selectedTier, frequency);
      if (result?.success) {
        onSuccess(result);
        onClose();
      } else {
        onError(result?.error || "Plan change failed");
      }
    } catch (err: any) {
      onError(err?.message || "Plan change failed");
    } finally {
      setChangingPlan(false);
    }
  };

  const handleEnterpriseSubmit = async () => {
    if (!entCompany || !entTeamSize) return;
    setEntSubmitting(true);
    try {
      await contactEnterprise({
        companyName: entCompany,
        teamSize: entTeamSize,
        message: entMessage,
      });
      setEntDone(true);
    } catch (err: any) {
      onError(err?.message || "Failed to submit inquiry");
    } finally {
      setEntSubmitting(false);
    }
  };

  // ---------- Renders ----------

  const renderCheckmark = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
      <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const renderFrequencyToggle = () => (
    <div className="billing-freq-toggle">
      <button
        type="button"
        className={`billing-freq-btn ${frequency === "annual" ? "active" : ""}`}
        onClick={() => setFrequency("annual")}
      >
        Annual <span className="billing-freq-save">Save 38%</span>
      </button>
      <button
        type="button"
        className={`billing-freq-btn ${frequency === "monthly" ? "active" : ""}`}
        onClick={() => setFrequency("monthly")}
      >
        Monthly
      </button>
    </div>
  );

  const renderTierCard = (tier: PlanTier) => {
    const isCurrent = tier === currentTier && hasSubscription;
    const isFeatured = tier === "pro";
    const isEnt = tier === "enterprise";
    const isFree = tier === "free";

    const price = isFree
      ? "$0"
      : isEnt
      ? "Custom"
      : frequency === "annual"
      ? `$${PRICING[tier as "pro" | "business"].annual}`
      : `$${PRICING[tier as "pro" | "business"].monthly}`;

    const period = isFree ? "forever" : isEnt ? "" : "/month";
    const billingNote =
      !isFree && !isEnt && frequency === "annual"
        ? `Billed $${PRICING[tier as "pro" | "business"].annualTotal}/year`
        : !isFree && !isEnt
        ? "Cancel anytime"
        : "";

    let btnText = "Get started";
    let btnClass = "billing-tier-btn";
    if (isCurrent) {
      btnText = "Current plan";
      btnClass += " billing-tier-btn--current";
    } else if (isEnt) {
      btnText = "Contact us";
    } else if (isFree) {
      btnText = "Free forever";
      btnClass += " billing-tier-btn--ghost";
    } else {
      btnText = hasSubscription
        ? isUpgrade(currentTier, tier)
          ? "Upgrade"
          : "Downgrade"
        : "Start free trial";
      btnClass += " billing-tier-btn--primary";
    }

    return (
      <div
        key={tier}
        className={`billing-tier-card${isFeatured ? " billing-tier-card--featured" : ""}${isCurrent ? " billing-tier-card--current" : ""}`}
      >
        {isFeatured && <div className="billing-tier-badge">Most popular</div>}
        <div className="billing-tier-name">{tierLabel(tier)}</div>
        <div className="billing-tier-price">
          <span className="billing-tier-amount">{price}</span>
          {period && <span className="billing-tier-period">{period}</span>}
        </div>
        {billingNote && <div className="billing-tier-note">{billingNote}</div>}

        <div className="billing-tier-divider" />

        <ul className="billing-tier-features">
          {TIER_FEATURES[tier].map((f, i) => (
            <li key={i}>
              {renderCheckmark()}
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          className={btnClass}
          disabled={isCurrent || (isFree && !hasSubscription)}
          onClick={() => handleSelectTier(tier)}
        >
          {btnText}
        </button>
      </div>
    );
  };

  // ---------- VIEW: Compare ----------
  if (view === "compare") {
    return (
      <div className="subscription-modal-overlay" onClick={onClose}>
        <div className="billing-modal-wide" onClick={(e) => e.stopPropagation()}>
          <h2>Choose your plan</h2>
          <p>Simple pricing for every team. Start free, upgrade when you're ready.</p>

          {renderFrequencyToggle()}

          <div className="billing-tier-grid">
            {TIER_ORDER.map(renderTierCard)}
          </div>

          <div className="payment-note">
            Secure checkout with
            <img src={StripeLogo} alt="Stripe" />
          </div>

          <button className="subscription-close-button" onClick={onClose} aria-label="Close" type="button">
            <FaTimes />
          </button>
        </div>
      </div>
    );
  }

  // ---------- VIEW: Checkout ----------
  if (view === "checkout" && selectedTier && (selectedTier === "pro" || selectedTier === "business")) {
    const p = PRICING[selectedTier];
    const isAnnual = frequency === "annual";

    return (
      <div className="subscription-modal-overlay" onClick={onClose}>
        <div className="subscription-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="subscription-checkout-selected">
            <div className="plan-summary-title">
              <div className="plan-summary-label">Selected plan</div>
              <div>
                <div className="plan-summary-selected">
                  {tierLabel(selectedTier)} {isAnnual ? "(Annual)" : "(Monthly)"}
                </div>
                <div
                  className="switch-plan"
                  onClick={() => setFrequency(isAnnual ? "monthly" : "annual")}
                >
                  {isAnnual ? "Switch to Monthly" : "Switch to Annual"}
                </div>
              </div>
            </div>

            <hr className="hr-checkout-divider" />

            <div className="plan-price-detail">
              <div className="plan-summary-label">Plan price</div>
              <div>
                <div className="plan-summary-selected">
                  {isAnnual ? `$${p.annualTotal}/year` : `$${p.monthly}/month`}
                </div>
                {isAnnual && (
                  <div className="plan-summary-description">
                    Works out to ${p.annual}/month
                  </div>
                )}
              </div>
            </div>
          </div>

          <StripeProvider>
            <NapkinCheckoutForm
              tier={selectedTier}
              frequency={frequency}
              onSuccess={(d) => {
                onSuccess(d);
                onClose();
              }}
              onError={onError}
            />
          </StripeProvider>

          <div className="subscription-back-button" onClick={() => setView("compare")}>
            Back to plan selection
          </div>

          <button className="subscription-close-button" onClick={onClose} aria-label="Close" type="button">
            <FaTimes />
          </button>
        </div>
      </div>
    );
  }

  // ---------- VIEW: Manage ----------
  if (view === "manage") {
    return (
      <div className="subscription-modal-overlay" onClick={onClose}>
        <div className="subscription-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="premium-member-section">
            <div className="premium-badge">
              <span className="premium-text">{tierLabel(currentTier)} member</span>
            </div>

            {renewalDate && (
              <p style={{ fontSize: 12, margin: "0 0 8px" }}>
                Renews on {new Date(renewalDate).toLocaleDateString()}
              </p>
            )}

            <div className="subscription-checkout-selected">
              <div className="plan-summary-title">
                <div className="plan-summary-label">Current plan</div>
                <div>
                  <div className="plan-summary-selected">
                    {tierLabel(currentTier)}{" "}
                    {currentFrequency === "annual" ? "(Annual)" : "(Monthly)"}
                  </div>
                </div>
              </div>

              {(currentTier === "pro" || currentTier === "business") && (
                <>
                  <hr className="hr-checkout-divider" />
                  <div className="plan-price-detail">
                    <div className="plan-summary-label">Plan price</div>
                    <div>
                      <div className="plan-summary-selected">
                        {currentFrequency === "annual"
                          ? `$${PRICING[currentTier].annualTotal}/year`
                          : `$${PRICING[currentTier].monthly}/month`}
                      </div>
                      {currentFrequency === "annual" && (
                        <div className="plan-summary-description">
                          Works out to ${PRICING[currentTier].annual}/month
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div
              className="switch-plan"
              style={{ marginTop: 8 }}
              onClick={() => setView("compare")}
            >
              Change plan
            </div>

            <div className="subscription-button-cancel" onClick={onCancel}>
              Cancel subscription
            </div>
          </div>

          <button className="subscription-close-button" onClick={onClose} aria-label="Close" type="button">
            <FaTimes />
          </button>
        </div>
      </div>
    );
  }

  // ---------- VIEW: Proration preview ----------
  if (view === "proration" && selectedTier) {
    const upgrade = isUpgrade(currentTier, selectedTier);

    return (
      <div className="subscription-modal-overlay" onClick={onClose}>
        <div className="subscription-modal-content" onClick={(e) => e.stopPropagation()}>
          <h2>{upgrade ? "Upgrade" : "Change"} to {tierLabel(selectedTier)}</h2>

          {prorationLoading ? (
            <div style={{ padding: "24px 0", textAlign: "center", color: "#6b7280", fontSize: 13 }}>
              Calculating your proration...
            </div>
          ) : prorationData ? (
            <div className="billing-proration-card">
              <div className="billing-proration-row">
                <span>Switching from</span>
                <span style={{ fontWeight: 600 }}>
                  {tierLabel(currentTier)} ({currentFrequency})
                </span>
              </div>
              <div className="billing-proration-row">
                <span>Switching to</span>
                <span style={{ fontWeight: 600 }}>
                  {tierLabel(selectedTier)} ({frequency})
                </span>
              </div>
              <div className="billing-proration-divider" />
              {upgrade && prorationData.credit !== 0 && (
                <div className="billing-proration-row">
                  <span>Credit from current plan</span>
                  <span style={{ color: "#059669" }}>
                    -{formatPrice(Math.abs(prorationData.credit))}
                  </span>
                </div>
              )}
              {upgrade && prorationData.newCharge !== 0 && (
                <div className="billing-proration-row">
                  <span>New plan charge (prorated)</span>
                  <span>{formatPrice(prorationData.newCharge)}</span>
                </div>
              )}
              <div className="billing-proration-divider" />
              <div className="billing-proration-row" style={{ fontWeight: 700 }}>
                <span>{upgrade ? "Amount due now" : "Next billing amount"}</span>
                <span>{formatPrice(prorationData.amountDue)}</span>
              </div>
              {!upgrade && (
                <p style={{ fontSize: 11.5, color: "#6b7280", margin: "8px 0 0" }}>
                  Your current plan stays active until the end of this billing period. The new price applies at your next renewal.
                </p>
              )}
            </div>
          ) : null}

          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button
              type="button"
              className="primary-button"
              style={{ flex: 1 }}
              disabled={prorationLoading || changingPlan}
              onClick={handleConfirmPlanChange}
            >
              {changingPlan ? "Processing..." : upgrade ? "Confirm upgrade" : "Confirm change"}
            </button>
            <button
              type="button"
              className="secondary-button"
              style={{ flex: 0 }}
              onClick={() => setView("compare")}
            >
              Back
            </button>
          </div>

          <button className="subscription-close-button" onClick={onClose} aria-label="Close" type="button">
            <FaTimes />
          </button>
        </div>
      </div>
    );
  }

  // ---------- VIEW: Enterprise contact ----------
  if (view === "enterprise") {
    return (
      <div className="subscription-modal-overlay" onClick={onClose}>
        <div className="subscription-modal-content" onClick={(e) => e.stopPropagation()}>
          {!entDone ? (
            <>
              <h2>Enterprise Plan</h2>
              <p>Tell us about your team and we'll create a custom plan.</p>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div className="form-row">
                  <label>COMPANY NAME</label>
                  <input
                    type="text"
                    value={entCompany}
                    onChange={(e) => setEntCompany(e.target.value)}
                    placeholder="Acme Corp"
                    className="billing-input"
                  />
                </div>
                <div className="form-row">
                  <label>TEAM SIZE</label>
                  <input
                    type="text"
                    value={entTeamSize}
                    onChange={(e) => setEntTeamSize(e.target.value)}
                    placeholder="e.g. 25"
                    className="billing-input"
                  />
                </div>
                <div className="form-row">
                  <label>MESSAGE (OPTIONAL)</label>
                  <textarea
                    value={entMessage}
                    onChange={(e) => setEntMessage(e.target.value)}
                    placeholder="Tell us about your needs..."
                    className="billing-input"
                    rows={3}
                    style={{ resize: "vertical" }}
                  />
                </div>
              </div>

              <button
                type="button"
                className="primary-button subscription-button"
                disabled={!entCompany || !entTeamSize || entSubmitting}
                onClick={handleEnterpriseSubmit}
              >
                {entSubmitting ? "Submitting..." : "Get in touch"}
              </button>
            </>
          ) : (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>{"\\u2713"}</div>
              <h2 style={{ marginBottom: 6 }}>We'll be in touch!</h2>
              <p style={{ color: "#6b7280" }}>
                Our team will reach out within 1 business day to discuss your Enterprise plan.
              </p>
            </div>
          )}

          <div className="subscription-back-button" onClick={() => setView("compare")}>
            Back to plans
          </div>

          <button className="subscription-close-button" onClick={onClose} aria-label="Close" type="button">
            <FaTimes />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default NapkinSubscriptionModal;
